/**
 * Turns a pasted-in lyric/chord sheet (plain text, ChordPro, or classic
 * "chords on their own line above the lyric") into the same shape W2 already
 * builds by hand: one row per section, each with lyrics and a chords array
 * anchored by {l, w}.
 *
 * This never touches Supabase — it's pure text in, section objects out — so
 * the caller decides how to insert them.
 */

import { normalizeKey, QUALITIES } from './chords'

// song_sections.section_type is a fixed DB enum: verse | chorus | bridge |
// bridge_2 | tag. Intro/Outro/Interlude/Pre-Chorus have no matching type, so
// they map to 'tag' — the closest existing catch-all — rather than being
// misread as front matter or merged into whichever section sits next to them.
// They still get their own section at import; only the label is collapsed.
const HEADER_MAP = [
  [/^bridge\s*2\b/i, 'bridge_2'],
  [/^pre[\s-]?chorus\b/i, 'tag'],
  [/^verse\b/i, 'verse'],
  [/^chorus\b/i, 'chorus'],
  [/^bridge\b/i, 'bridge'],
  [/^tag\b/i, 'tag'],
  [/^intro\b/i, 'tag'],
  [/^outro\b/i, 'tag'],
  [/^interlude\b/i, 'tag'],
  [/^instrumental\b/i, 'tag'],
]

/** "Verse 1", "[Chorus]", "BRIDGE:" — the punctuation a pasted sheet wears. */
function matchHeader(line) {
  const stripped = line.trim().replace(/^\[|\]$/g, '').replace(/:$/, '').trim()
  if (!stripped || stripped.length > 24) return null
  for (const [re, type] of HEADER_MAP) {
    if (re.test(stripped)) return type
  }
  return null
}

/**
 * The front matter and footer every real-world lyric site wears: title,
 * key/capo/tempo, CCLI number, copyright, writer credits. None of it is sung,
 * so none of it belongs in a section's lyrics.
 */
const METADATA_RE = [
  /^key\s*[:-]/i,
  /^capo\s*[:-]/i,
  /^tempo\s*[:-]/i,
  /^bpm\s*[:-]/i,
  /^time\s*sig(nature)?\s*[:-]/i,
  /^ccli\b/i,
  /^©|^\(c\)\s|copyright|all rights reserved/i,
  /^(written|words?|music|lyrics?|arranged)\s+(by|and)\b/i,
  /^(artist|performed by|album|recorded by)\s*[:-]/i,
]

/** A guitar tab line — six strings of dashes and fret numbers, not lyrics. */
function isTabLine(line) {
  const t = line.trim()
  if (!t) return false
  if (/^[eEBGDAeb]\|/.test(t) || /^[eEBGDAeb]\s*[-|]/.test(t)) return true
  const dashes = (t.match(/-/g) ?? []).length
  return dashes >= 4 && dashes / t.length > 0.3
}

const isMetadataLine = (line) => METADATA_RE.some((re) => re.test(line.trim())) || isTabLine(line)

const normalizeForMatch = (s) => String(s ?? '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()

/** "Amazing Grace" vs the title the writer already typed in — allows either
 * to be the looser one (a subtitle, a trailing "(Live)"), not just exact. */
function looksLikeTitle(line, title) {
  const a = normalizeForMatch(line)
  const b = normalizeForMatch(title)
  if (!a || !b) return false
  return a === b || a.includes(b) || b.includes(a)
}

const NOTE_SEMITONE = {
  C: 0, 'B#': 0, 'C#': 1, Db: 1, D: 2, 'D#': 3, Eb: 3, E: 4, Fb: 4,
  F: 5, 'E#': 5, 'F#': 6, Gb: 6, G: 7, 'G#': 8, Ab: 8, A: 9, 'A#': 10,
  Bb: 10, B: 11, Cb: 11,
}
const DEGREE_OFFSETS = [0, 2, 4, 5, 7, 9, 11]

function degreeForSemitone(key, semitone) {
  const keySemi = NOTE_SEMITONE[normalizeKey(key) ?? 'C']
  let best = null
  for (let d = 1; d <= 7; d++) {
    const scaleSemi = (keySemi + DEGREE_OFFSETS[d - 1]) % 12
    let delta = ((semitone - scaleSemi) % 12 + 12) % 12
    if (delta > 6) delta -= 12
    if (best === null || Math.abs(delta) < Math.abs(best.a)) best = { d, a: delta }
  }
  return best
}

// Longest suffixes first, so "maj7" isn't swallowed by an empty-string match.
const QUALITY_BY_SUFFIX = [...QUALITIES].sort((a, b) => b.c.length - a.c.length)

const CHORD_RE = /^([A-G])(#|b)?([A-Za-z0-9°+]*)(?:\/([A-G])(#|b)?)?$/

/** "G", "D/F#", "Em7", "Csus4" → the {d,a,q,b,ba} shape putChord expects. */
export function parseChordToken(token, key) {
  const m = CHORD_RE.exec(token.trim())
  if (!m) return null
  const [, root, rootAcc, suffix, bassRoot, bassAcc] = m
  const rootName = root + (rootAcc ?? '')
  const rootSemi = NOTE_SEMITONE[rootName]
  if (rootSemi === undefined) return null
  const quality = QUALITY_BY_SUFFIX.find((q) => q.c.toLowerCase() === suffix.toLowerCase())
  if (!quality && suffix) return null
  const { d, a } = degreeForSemitone(key, rootSemi)
  const chord = { d, a, q: (quality ?? QUALITIES[0]).id }
  if (bassRoot) {
    const bassName = bassRoot + (bassAcc ?? '')
    const bassSemi = NOTE_SEMITONE[bassName]
    if (bassSemi !== undefined) {
      const bass = degreeForSemitone(key, bassSemi)
      chord.b = bass.d
      chord.ba = bass.a
    }
  }
  return chord
}

function isChordLine(line) {
  const tokens = line.trim().split(/\s+/).filter(Boolean)
  if (tokens.length === 0) return false
  return tokens.every((t) => CHORD_RE.test(t))
}

/** Word start columns for a lyric line, matching the split lyricLines() uses. */
function wordColumns(line) {
  const cols = []
  const re = /\S+/g
  let m
  while ((m = re.exec(line))) cols.push(m.index)
  return cols
}

function chordTokenColumns(line) {
  const out = []
  const re = /\S+/g
  let m
  while ((m = re.exec(line))) out.push({ token: m[0], col: m.index })
  return out
}

/**
 * [G]Amazing [D]grace → strips the brackets and anchors each chord to a word.
 *
 * The stored chord model only has word-level anchors (l, w) — there's no way
 * to represent "on the second syllable" — so a bracket dropped mid-word
 * (Ama[G]zing) attaches to that whole word rather than splitting it. That's a
 * simplification, not a bug: splitting the word would invent a data point the
 * schema can't hold anyway.
 */
function parseInlineLine(line, key, lineIndex) {
  const plain = line.replace(/\[([^\]]+)\]/g, '')
  const words = plain.split(/\s+/).filter(Boolean)
  const wordStarts = []
  { let i = 0; for (const w of words) { i = plain.indexOf(w, i); wordStarts.push(i); i += w.length } }

  const chords = []
  const re = /\[([^\]]+)\]/g
  let m
  let strippedSoFar = 0 // chars removed by brackets already consumed
  while ((m = re.exec(line))) {
    const ch = parseChordToken(m[1], key)
    if (ch) {
      const plainOffset = m.index - strippedSoFar
      // The word this offset falls inside or immediately after — not the next
      // word along, so a bracket dropped mid-word still lands on that word.
      let w = 0
      for (let i = 0; i < wordStarts.length; i++) {
        if (wordStarts[i] <= plainOffset) w = i
        else break
      }
      if (words.length > 0) chords.push({ ...ch, l: lineIndex, w })
    }
    strippedSoFar += m[0].length
  }
  return { text: words.join(' '), chords }
}

/**
 * A sheet that actually uses "Verse 1" / "[Chorus]" style labels — those
 * labels ARE the section boundaries, so blank lines inside a block are just
 * a stanza break within one section, not a new one.
 *
 * Before the first header, a headered sheet carries only front matter —
 * title, key, capo, writer credits — never lyrics meant to be sung. Dropping
 * it outright beats inventing a phantom "Verse 1" out of the title.
 */
function headeredBlocks(lines) {
  const blocks = []
  let current = null
  let seenHeader = false
  for (const line of lines) {
    const type = matchHeader(line)
    if (type) {
      current = { type, raw: [] }
      blocks.push(current)
      seenHeader = true
      continue
    }
    if (!seenHeader) continue // front matter ahead of the first header
    if (isMetadataLine(line)) continue // CCLI #, copyright, tab notation, etc.
    current.raw.push(line)
  }
  return blocks
}

/**
 * No "Verse 1", no "[Chorus]" — most real chord sheets someone photographs or
 * pastes in don't bother. Dumping the whole thing into one section reads as a
 * single run-on block regardless of how well line breaks survive extraction,
 * so instead: every blank line ends a section, same as most people's sense of
 * a stanza. A block whose words repeat an earlier block word-for-word is
 * relabelled 'chorus' — verses don't repeat, choruses do, and that's a
 * reliable enough signal without asking the sheet to say so.
 */
function blankLineBlocks(lines, title) {
  const blocks = []
  let current = null
  let titleChecked = false
  for (const line of lines) {
    if (isMetadataLine(line)) continue
    if (!titleChecked && line.trim()) {
      titleChecked = true
      if (looksLikeTitle(line, title)) continue
    }
    if (!line.trim()) { current = null; continue } // blank line ends the block
    if (!current) { current = { type: 'verse', raw: [] }; blocks.push(current) }
    current.raw.push(line)
  }

  const firstSeen = new Map() // normalized lyric text -> the block that first had it
  for (const block of blocks) {
    const words = block.raw.filter((l) => !isChordLine(l)).join(' ')
    const norm = normalizeForMatch(words)
    if (!norm) continue
    const earlier = firstSeen.get(norm)
    if (earlier) { earlier.type = 'chorus'; block.type = 'chorus' } else { firstSeen.set(norm, block) }
  }
  return blocks
}

/**
 * Parses a whole sheet into sections.
 *
 * `title` is what the writer already typed into W1 — passed through so a
 * headerless paste can still drop its own title line, which no metadata
 * pattern can catch on its own (nothing marks a title as a title except that
 * it matches what's already on record).
 */
export function parseSheetFile(raw, key = 'C', title = '') {
  const lines = String(raw).replace(/\r\n/g, '\n').split('\n')
  const hasHeaders = lines.some((l) => matchHeader(l))

  const blocks = hasHeaders
    ? headeredBlocks(lines)
    : blankLineBlocks(lines, title)

  const counters = {}
  const sections = []
  for (const block of blocks) {
    const outLines = []
    const chords = []
    const raw = block.raw
    for (let i = 0; i < raw.length; i++) {
      const line = raw[i]
      if (/\[[^\]]+\]/.test(line)) {
        const { text, chords: lineChords } = parseInlineLine(line, key, outLines.length)
        outLines.push(text)
        chords.push(...lineChords)
        continue
      }
      if (isChordLine(line) && i + 1 < raw.length && !isChordLine(raw[i + 1]) && raw[i + 1].trim()) {
        const lyricLine = raw[i + 1]
        const lineIndex = outLines.length
        const tokens = chordTokenColumns(line)
        const cols = wordColumns(lyricLine)
        for (const { token, col } of tokens) {
          const ch = parseChordToken(token, key)
          if (!ch || cols.length === 0) continue
          let w = 0
          let best = Infinity
          cols.forEach((c, idx) => {
            const d = Math.abs(c - col)
            if (d < best) { best = d; w = idx }
          })
          chords.push({ ...ch, l: lineIndex, w })
        }
        outLines.push(lyricLine)
        i++ // consumed the lyric line too
        continue
      }
      outLines.push(line)
    }
    // Trim leading/trailing blank lines the block picked up around its header.
    while (outLines.length && !outLines[0].trim()) outLines.shift()
    while (outLines.length && !outLines[outLines.length - 1].trim()) outLines.pop()
    if (outLines.length === 0 && chords.length === 0) continue

    counters[block.type] = (counters[block.type] ?? 0) + 1
    sections.push({
      section_type: block.type,
      section_number: counters[block.type],
      lyrics: outLines.join('\n'),
      chords,
    })
  }
  return sections
}
